// GamingScreen — become a gamer. Each week pick one move: PRACTICE (spend energy to
// raise your skill, no cash) or COMPETE (pay a tournament entry, win a skill-scaled
// prize). The lesson: skill is the investment. Luck only nudges the result — a
// practised player wins reliably, a beginner usually loses the entry. Cross the pro
// line and streaming pays you passively every week, whatever you do.
//
// Resolves at the next weekly settle → income lands in the week's cashflow → Payday.

import {
  practiceGain,
  tournamentPrize,
  expectedTournamentNet,
  streamIncome,
  isPro,
  PRACTICE_ENERGY,
  TOURNAMENT_ENTRY,
  PRO_SKILL,
  type GamingAction,
} from '@biz-kid/engine';
import { useDispatch, useGameState, selectHappiness, selectCash, selectGaming } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, money, Bar } from '../../ui/kit.js';
import { CartoonGamingScene as GamingScene } from '../../ui/cartoon.js';

export function GamingScreen({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const gaming = useGameState(selectGaming);
  const energy = useGameState(selectHappiness);
  const cash = useGameState(selectCash);

  const skill = gaming.skill;
  const pro = isPro(skill);
  const stream = streamIncome(skill);
  const canPractice = energy >= PRACTICE_ENERGY;
  const canCompete = cash >= TOURNAMENT_ENTRY;
  const expectedNet = expectedTournamentNet(skill);
  const prizeLow = tournamentPrize(skill, 0); // unlucky
  const prizeHigh = tournamentPrize(skill, 0.99); // lucky

  const start = () => dispatch({ kind: 'StartGaming' });
  const pick = (action: GamingAction) => dispatch({ kind: 'SetGamingAction', action });

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">Beat lab · royalties on repeat</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>🎧 Beat Lab</h1>
          </div>
          <Button tone="ghost" size="sm" onClick={onClose}>← Home</Button>
        </header>

        {/* Voxel atmosphere — Miko at the rig (skill is the investment). */}
        <Card pad={0} style={{ overflow: 'hidden', marginBottom: 14 }}>
          <GamingScene />
        </Card>

        {!gaming.active ? (
          <Card>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
              Produce beats, level up your skill, sell licences — go pro and the royalties pay you on repeat.
            </p>
            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" onClick={start}>Make your first beat →</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Skill */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <Eyebrow color={pro ? 'var(--up)' : 'var(--brand)'}>Skill</Eyebrow>
                <span className="bk-tnum" style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)' }}>{skill} / 100</span>
              </div>
              <Bar frac={skill / 100} color={pro ? 'var(--up)' : 'var(--brand)'} />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {pro
                  ? <Chip tone="up">🏆 Pro · streaming pays {money(stream)}/wk</Chip>
                  : <Chip tone="neutral">Go pro at {PRO_SKILL} skill → passive streaming income</Chip>}
                <Chip tone="brand">Next practice +{practiceGain(skill)} skill</Chip>
              </div>
              {!pro && (
                <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  💡 Each practice adds less than the last — mastery gets harder the better you are. The last
                  stretch to pro is the toughest, and the most worth it.
                </p>
              )}
            </Card>

            {/* This week's move */}
            <Card style={{ marginBottom: 14 }}>
              <Eyebrow>This week's move</Eyebrow>
              <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                <MoveRow
                  emoji="🎯"
                  title="Practise"
                  sub={`+${practiceGain(skill)} skill · costs ${PRACTICE_ENERGY} energy · no cash`}
                  selected={gaming.weekAction === 'practice'}
                  disabled={!canPractice}
                  disabledNote={!canPractice ? 'Too tired — take a break first' : undefined}
                  onClick={() => pick('practice')}
                />
                <MoveRow
                  emoji="🏟️"
                  title="Compete"
                  sub={`Entry ${money(TOURNAMENT_ENTRY)} · prize ${money(prizeLow)}–${money(prizeHigh)} (skill-scaled)`}
                  selected={gaming.weekAction === 'compete'}
                  disabled={!canCompete}
                  disabledNote={!canCompete ? `Need ${money(TOURNAMENT_ENTRY)} to enter` : undefined}
                  onClick={() => pick('compete')}
                />
                <MoveRow
                  emoji="😴"
                  title="Rest this week"
                  sub={pro ? 'Still earn streaming income — no skill gain' : 'Do nothing — save your energy'}
                  selected={gaming.weekAction === 'rest'}
                  onClick={() => pick('rest')}
                />
              </div>
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                Your move plays out at <b style={{ color: 'var(--text)' }}>payday</b> (the weekly settle), like the rest of
                your income.
              </p>
            </Card>

            {/* Compete odds — skill vs luck */}
            <Card style={{ marginBottom: 14 }}>
              <Eyebrow color={expectedNet >= 0 ? 'var(--up)' : 'var(--down)'}>Your tournament odds</Eyebrow>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '8px 0' }}>
                <span style={{ fontSize: 14, color: 'var(--text-2)' }}>Typical result (after the {money(TOURNAMENT_ENTRY)} entry)</span>
                <span style={{ fontSize: 17, fontWeight: 900, color: expectedNet >= 0 ? 'var(--up)' : 'var(--down)' }}>
                  {expectedNet >= 0 ? '+' : ''}{money(expectedNet)}
                </span>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
                💡 {expectedNet >= 0
                  ? 'At your skill, you usually come out ahead — practice turned the bet into a paycheck.'
                  : 'At your skill, you usually lose the entry. This is not gambling — get better first, then the prizes follow.'}
              </p>
            </Card>

            {/* Lesson */}
            <Card>
              <Eyebrow color="var(--warn)">Skill is the investment</Eyebrow>
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
                💡 Anyone can enter a tournament — but only the practised win. The hours you put into getting
                good are an <b style={{ color: 'var(--text)' }}>investment in yourself</b>: they raise what every
                future hour is worth. Go pro and your audience pays you to do what you love, even on your days off.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function MoveRow({
  emoji, title, sub, selected, disabled, disabledNote, onClick,
}: {
  emoji: string;
  title: string;
  sub: string;
  selected: boolean;
  disabled?: boolean;
  disabledNote?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        padding: 12, borderRadius: 14,
        border: selected ? '2px solid var(--brand)' : '1px solid var(--border)',
        background: selected ? 'var(--brand-soft)' : 'var(--surface-2)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit', opacity: disabled ? 0.55 : 1,
      }}
    >
      <span style={{ fontSize: 22, flexShrink: 0 }}>{emoji}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)' }}>{disabledNote ?? sub}</span>
      </span>
      {selected && <span style={{ flexShrink: 0, fontSize: 13, fontWeight: 800, color: 'var(--brand)' }}>✓ Chosen</span>}
    </button>
  );
}
